import { useCallback, useMemo, useState } from 'react';
import type { ColumnSort, SortPreset } from '@/domain';
import { useTablePreferences } from '../container';

export interface TableOrder<Row> {
  /** What the select shows: a preset key, or empty while a column is driving. */
  presetKey: string;
  /** What the table shows as sorted, or null while a preset is driving. */
  column: ColumnSort | null;
  /** The reader picked a preset: it takes over from any column. */
  choose: (key: string) => void;
  /** The reader clicked a header, or the phone's column select: the column takes over. */
  sortByColumn: (next: ColumnSort | null) => void;
  /** The preset applied, in memory. Rows unchanged while a column is driving. */
  apply: (rows: Row[]) => Row[];
  /** The `sort` query parameter for a server-ordered table, or undefined. */
  sortParam: string | undefined;
}

/**
 * The one sort state a case table has. Section 3 of the request of
 * 13 September 2026.
 *
 * A preset and a column cannot disagree because there is only ever one of them:
 * picking a preset clears the column, clicking a header clears the preset, and
 * while a column is driving the select says so rather than naming an order the
 * table is not in. They were going to be two pieces of state, the select above
 * the table and the table's own header sort, and two pieces of state that both
 * claim to order the same rows is a bug waiting for the first person to use
 * both.
 *
 * The FIRST preset is the default: newest first on every list, since section 2
 * of the request of 5 October 2026. A reader who chose another order finds it
 * again, because the choice is remembered per list.
 */
export function useTableOrder<Row>(
  tableKey: string,
  presets: readonly SortPreset<Row>[],
): TableOrder<Row> {
  const preferences = useTablePreferences();

  const [presetKey, setPresetKey] = useState(() => {
    const fallback = presets[0]?.key ?? '';
    const remembered = preferences.order(tableKey);
    return presets.some((preset) => preset.key === remembered) ? (remembered as string) : fallback;
  });
  const [column, setColumn] = useState<ColumnSort | null>(null);

  const choose = useCallback(
    (key: string) => {
      setPresetKey(key);
      setColumn(null);
      preferences.rememberOrder(tableKey, key);
    },
    [preferences, tableKey],
  );

  const sortByColumn = useCallback((next: ColumnSort | null) => {
    setColumn(next);
  }, []);

  const active = presets.find((preset) => preset.key === presetKey);
  const compare = active?.compare;

  const apply = useCallback(
    (rows: Row[]) => {
      if (column !== null || compare === undefined) return rows;
      return [...rows].sort(compare);
    },
    [column, compare],
  );

  return useMemo(
    () => ({
      presetKey: column === null ? presetKey : '',
      column,
      choose,
      sortByColumn,
      apply,
      sortParam: column === null ? active?.sortParam : undefined,
    }),
    [column, presetKey, choose, sortByColumn, apply, active],
  );
}
